/**
 * seoData.js
 * Central SEO config for every public route.
 *
 * Structure per entry:
 *   title        — <title> tag. Keep under 60 chars.
 *   description  — meta description. 150–160 chars.
 *   canonical    — path (BASE_URL prepended by PageSEO)
 *   og.image     — path to hero image used for social sharing
 *   jsonLd       — page-specific JSON-LD (optional; global org schema stays in index.html)
 *
 * Program pages use buildProgramSEO() so each slug auto-generates from
 * the PROGRAMS_CATALOG — no manual entry needed per program.
 *
 * PROGRAMS_CATALOG is imported LAZILY via ensureProgramSEO() so the
 * ~15 KB program-catalog dataset stays out of the main bundle. Non-
 * program routes get their SEO synchronously from STATIC_SEO. Program
 * routes get a lightweight sync fallback initially, then the full
 * JSON-LD-carrying entry after ensureProgramSEO() resolves (see
 * components/RouteHelmet.jsx). Prerender-cache captures the post-
 * resolve DOM (Puppeteer waits for networkidle + WAIT_MS), so bots
 * still receive the full Course + EducationalOccupationalProgram
 * @graph in their served HTML.
 */

const BASE = 'https://andersoncollege.com';

// Word-boundary meta-description trimmer — shared by every auto-SEO
// path (programs, blog, etc.) so no bot ever indexes a mid-word cut.
import { trimMetaDescription } from '../utils/trimMetaDescription';
import { SOCIAL_URLS } from './social';

// ─────────────────────────────────────────────
// Helpers: Course + EducationalOccupationalProgram JSON-LD
// ─────────────────────────────────────────────
//
// Program pages emit a `@graph` containing BOTH nodes so Google can
// resolve `Course` (for the Courses Rich Result) AND
// `EducationalOccupationalProgram` (for the newer program-level rich
// result that surfaces credential, duration, and NOC occupational
// category). Both share the same #organization @id reference so Google
// treats them as the same provider.
function deliveryToProgramMode(delivery) {
  if (delivery === 'Online') return 'online';
  if (delivery === 'Hybrid') return 'hybrid';
  return 'onsite';
}

function courseNode({ name, description, slug, delivery, durationWeeks, credential, classroomHours, placementHours }) {
  // 2026-02 structured-data cleanup:
  //   • courseMode now matches the visible delivery label on the
  //     program page: On-Campus → "onsite", Online → "online",
  //     Hybrid → "blended". Previously Hybrid emitted the array
  //     ["online","onsite"] which Rich Results Test treats as valid
  //     but Google's docs flag "blended" as the correct single-value
  //     equivalent for hybrid programs.
  //   • courseWorkload is now computed from the authoritative
  //     per-program classroomHours + placementHours (added to the
  //     PROGRAMS_CATALOG entries where known). Falls back to omit
  //     when hours aren't available — rather than fabricating a
  //     25h/week approximation that drifted from the visible facts.
  const courseMode = delivery === 'Online' ? 'online' : delivery === 'Hybrid' ? 'blended' : 'onsite';
  const totalHours = (typeof classroomHours === 'number' && typeof placementHours === 'number')
    ? classroomHours + placementHours
    : null;
  const courseInstance = {
    '@type': 'CourseInstance',
    courseMode,
  };
  if (totalHours !== null) {
    courseInstance.courseWorkload = `PT${totalHours}H`;
  }
  // Attach provider's postal address for onsite / hybrid instances so Google
  // can resolve a Place for the Course rich result. Online-only courses omit
  // location per schema.org guidance.
  if (delivery !== 'Online') {
    courseInstance.location = {
      '@type': 'EducationalOrganization',
      '@id': `${BASE}/#organization`,
      name: 'Anderson College of Health, Business and Technology',
    };
  }
  return {
    '@type': 'Course',
    name,
    description,
    url: `${BASE}/programs/${slug}`,
    provider: {
      '@type': 'EducationalOrganization',
      '@id': `${BASE}/#organization`,
      name: 'Anderson College of Health, Business and Technology',
    },
    courseMode,
    timeRequired: `P${durationWeeks}W`,
    educationalCredentialAwarded: credential,
    inLanguage: 'en-CA',
    hasCourseInstance: [courseInstance],
    offers: {
      '@type': 'Offer',
      category: 'Tuition',
      url: `${BASE}/programs/${slug}`,
    },
  };
}

function educationalOccupationalProgramNode({ name, slug, delivery, durationWeeks, credential, nocMap }) {
  const url = `${BASE}/programs/${slug}`;
  const node = {
    '@type': 'EducationalOccupationalProgram',
    name,
    url,
    provider: { '@id': `${BASE}/#organization` },
    programType: 'Diploma',
    educationalProgramMode: deliveryToProgramMode(delivery),
    timeToComplete: `P${durationWeeks}W`,
    occupationalCredentialAwarded: credential,
    offers: {
      '@type': 'Offer',
      category: 'Tuition',
      priceCurrency: 'CAD',
      url,
    },
  };

  // Attach occupationalCategory ONLY when the slug has a verified NOC
  // 2021 mapping. Programs without a confirmed NOC in the map omit the
  // field entirely — no empty strings, no nulls (per user constraint:
  // do NOT invent labels).
  const noc = nocMap[slug];
  if (noc && noc.code && noc.label) {
    node.occupationalCategory = {
      '@type': 'CategoryCode',
      codeValue: noc.code,
      name: noc.label,
      inCodeSet: {
        '@type': 'CategoryCodeSet',
        name: 'NOC 2021',
      },
    };
  }

  return node;
}

function programGraphJsonLd(program, credential, nocMap) {
  // Short display name — strip "Diploma" / "— H.A.R.P. Approved" etc.
  // for the breadcrumb label so Google surfaces a clean leaf like
  // "Paralegal" rather than "Paralegal Diploma". Matches the H1 the
  // page actually renders.
  const shortName = program.name.replace(/ Diploma.*$/, '').replace(/ – .*$/, '');
  const breadcrumb = {
    '@type': 'BreadcrumbList',
    '@id': `${BASE}/programs/${program.slug}#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home',     item: `${BASE}/` },
      { '@type': 'ListItem', position: 2, name: 'Programs', item: `${BASE}/programs` },
      { '@type': 'ListItem', position: 3, name: shortName,  item: `${BASE}/programs/${program.slug}` },
    ],
  };
  return {
    '@context': 'https://schema.org',
    '@graph': [
      courseNode({
        name: program.name,
        description: program.description,
        slug: program.slug,
        delivery: program.delivery,
        durationWeeks: program.durationWeeks,
        credential,
        classroomHours: program.classroomHours,
        placementHours: program.placementHours,
      }),
      educationalOccupationalProgramNode({
        name: program.name,
        slug: program.slug,
        delivery: program.delivery,
        durationWeeks: program.durationWeeks,
        credential,
        nocMap,
      }),
      breadcrumb,
    ],
  };
}

// ─────────────────────────────────────────────
// Auto-generate SEO for every program slug
// ─────────────────────────────────────────────
function buildProgramSEO(program, credential, nocMap) {
  const { slug, name, tagline, heroImage, delivery, durationWeeks } = program;

  // Strip trailing "Diploma" / "– H.A.R.P. Approved" etc. for cleaner titles
  const shortName = name.replace(/ Diploma.*$/, '').replace(/ – .*$/, '');
  const deliveryLabel = delivery === 'Online' ? 'Online' : delivery === 'Hybrid' ? 'Hybrid' : 'On-Campus';

  // Title kept ≤ 70 chars to fit Google SERP. If "<shortName> Diploma | Anderson College Ontario"
  // exceeds the limit, fall back to "<shortName> | Anderson College" (the longest variant of
  // shortName + brand that still fits typical names).
  const longTitle = `${shortName} Diploma | Anderson College Ontario`;
  const title = longTitle.length <= 70
    ? longTitle
    : `${shortName} | Anderson College`;

  return {
    title,
    description: trimMetaDescription(
      `${tagline} ${durationWeeks}-week ${deliveryLabel} program at Anderson College. 10 campuses across Ontario. Enrol today.`,
      160
    ),
    canonical: `/programs/${slug}`,
    og: {
      image: heroImage,
      imageAlt: `${shortName} program at Anderson College`,
    },
    jsonLd: programGraphJsonLd({
      name: program.name,
      description: tagline,
      slug: program.slug,
      delivery,
      durationWeeks,
    }, credential, nocMap),
  };
}

// ─────────────────────────────────────────────
// Lazy-loaded program SEO map
// ─────────────────────────────────────────────
// The PROGRAMS_CATALOG dataset is ~15 KB gzipped and its inclusion in
// seoData.js used to hoist the entire catalog into the main JS chunk
// (RouteHelmet is eager-mounted). Deferring the import keeps the main
// bundle lean; the catalog only loads when the user navigates to a
// /programs/:slug route, at which point RouteHelmet calls
// ensureProgramSEO() and re-renders with the JSON-LD-enabled SEO.
let _programSEOMap = null;
let _programSEOPromise = null;

/**
 * Loads (once) and returns the program-SEO map keyed by slug.
 * Returns a cached Promise on repeat calls in-flight, a resolved
 * value on subsequent calls. Safe to call on every route change.
 *
 * @returns {Promise<Object<string, object>>}
 */
export function ensureProgramSEO() {
  if (_programSEOMap) return Promise.resolve(_programSEOMap);
  if (_programSEOPromise) return _programSEOPromise;
  _programSEOPromise = import('@/data/programsCatalog').then((mod) => {
    const { PROGRAMS_CATALOG, PROGRAM_CREDENTIAL, PROGRAM_NOC_MAP } = mod;
    _programSEOMap = Object.fromEntries(
      PROGRAMS_CATALOG.map((p) => [p.slug, buildProgramSEO(p, PROGRAM_CREDENTIAL, PROGRAM_NOC_MAP)])
    );
    return _programSEOMap;
  });
  return _programSEOPromise;
}

/**
 * Sync accessor for the program SEO map. Returns null if the map has
 * not been loaded yet — callers should treat null as "use fallback SEO
 * and kick off ensureProgramSEO() to re-render". Once resolved, this
 * returns the same object reference forever.
 */
export function getLoadedProgramSEO() {
  return _programSEOMap;
}

// ─────────────────────────────────────────────
// Static page SEO map
// ─────────────────────────────────────────────
export const STATIC_SEO = {

  // ── Home ──────────────────────────────────
  '/': {
    title: 'Healthcare, Business & Technology College | Anderson College Ontario',
    // ≤155 chars so Google doesn't truncate in the SERP (2026-10 audit).
    // Program count locked at 23 — matches the visible count on /programs.
    description: 'Anderson College offers 23 career-focused diplomas across 10 Ontario campuses. 140+ years of excellence. Book a free advisor appointment today.',
    canonical: '/',
    og: {
      image: '/assets/brand/hero-graduate.jpg',
      imageAlt: 'Anderson College graduate holding her diploma',
    },
    // Site-level identity graph — EducationalOrganization + WebSite only.
    // Lives here (home-only) so Google doesn't see duplicate Organization
    // objects on every page. Program/campus pages reference the same
    // @id via { "@id": "https://andersoncollege.com/#organization" }.
    // Removed 2026-02 per structured-data cleanup:
    //   - hasOfferCatalog (23 invalid Course items — moved to /programs ItemList)
    //   - 10 campus CollegeOrUniversity (moved to /campuses/<slug>)
    //   - aggregateRating (rating not visibly shown on home page)
    //   - www.andersoncollege.com from sameAs (same host as canonical)
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'EducationalOrganization',
          '@id': `${BASE}/#organization`,
          name: 'Anderson College of Health, Business and Technology',
          alternateName: 'Anderson College',
          url: `${BASE}/`,
          logo: `${BASE}/assets/brand/logo-horizontal-white.png`,
          image: `${BASE}/assets/brand/hero-graduate.jpg`,
          description:
            "Anderson College is one of Canada's oldest colleges, having opened our first location in 1885. We have trained more than 150,000 students over 140+ years, and have been an empowering partner for students overcoming career challenges and transitioning to the next phase of their lives.",
          foundingDate: '1885',
          slogan: 'Your Training. Your Career. Your Future.',
          telephone: '+1-833-787-1885',
          email: 'info@andersoncollege.com',
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'CA',
            addressRegion: 'ON',
            addressLocality: 'Toronto',
          },
          sameAs: [
            // Social URLs come from `data/social.js` so the home
            // Organization, the footer, and every campus share one
            // list. The Wikipedia reference stays as a non-social
            // authority link. See 2026-02 structured-data cleanup.
            ...SOCIAL_URLS,
            'https://en.wikipedia.org/wiki/Anderson_College_of_Health,_Business_and_Technology',
          ],
          areaServed: { '@type': 'AdministrativeArea', name: 'Ontario, Canada' },
          hasCredential: [
            {
              '@type': 'EducationalOccupationalCredential',
              name: 'Registered under Ontario Career Colleges Act, 2005',
            },
          ],
        },
        {
          '@type': 'WebSite',
          '@id': `${BASE}/#website`,
          url: `${BASE}/`,
          name: 'Anderson College',
          publisher: { '@id': `${BASE}/#organization` },
          potentialAction: {
            '@type': 'SearchAction',
            target: `${BASE}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
          inLanguage: 'en-CA',
        },
      ],
    },
  },

  // ── Programs ──────────────────────────────
  '/programs': {
    title: 'All Programs | Anderson College Ontario',
    description: 'Browse 23 diploma programs in healthcare, business, and technology at Anderson College. On-campus, online, and hybrid options across 10 Ontario campuses.',
    canonical: '/programs',
    og: { image: '/assets/brand/hero-graduate.jpg' },
    // Course-list summary page per Google's structured-data guidance:
    // https://developers.google.com/search/docs/appearance/structured-data/course-info#course-list-summary-page
    // Each ListItem has only position + url pointing to the detailed
    // program page where the full Course object lives. We intentionally
    // do NOT embed Course objects here — that was the pattern that
    // produced 22 invalid Course items in the Rich Results Test.
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      url: `${BASE}/programs`,
      itemListOrder: 'https://schema.org/ItemListUnordered',
      itemListElement: [
        'personal-support-worker',
        'medical-office-administrator',
        'medical-office-administrator-online',
        'medical-laboratory-assistant',
        'medical-laboratory-technology',
        'cardiology-technologist',
        'diagnostic-medical-sonographer',
        'pharmacy-technician',
        'pharmacy-assistant',
        'intra-oral-dental-assistant',
        'medical-esthetician',
        'early-childhood-education',
        'early-childcare-assistant',
        'health-information-management',
        'business-administration',
        'accounting-and-payroll-administration',
        'supply-chain-logistics',
        'information-technology-technician',
        'computer-system-technician-networking',
        'orthopaedic-technician',
        'paralegal',
        'law-enforcement-police-foundations',
        'occupational-health-and-safety',
      ].map((slug, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${BASE}/programs/${slug}`,
      })),
    },
  },

  // ── Admissions ────────────────────────────
  '/admissions': {
    title: 'Admissions | Anderson College Ontario',
    description: 'Start your application to Anderson College. Learn about admission requirements, application steps, and how to get started in your new career.',
    canonical: '/admissions',
    og: { image: '/assets/heroes/how-to-apply-hero.jpg' },
  },
  '/admissions/how-to-apply': {
    title: 'How to Apply | Anderson College Ontario',
    description: 'Step-by-step guide to applying at Anderson College. Complete your application online in minutes. No entrance exam required for most programs.',
    canonical: '/admissions/how-to-apply',
    og: { image: '/assets/heroes/how-to-apply-hero.jpg' },
  },
  '/admissions/international-students': {
    title: 'International Students | Anderson College Ontario',
    description: 'International students are welcome at Anderson College. Learn about study permit requirements, pathway programs, and support services across our Ontario campuses.',
    canonical: '/admissions/international-students',
    og: { image: '/assets/heroes/admissions-international-hero.webp' },
  },

  // ── Why Anderson ──────────────────────────
  '/why-anderson': {
    title: 'Why Choose Anderson College | 140 Years of Career Training',
    description: 'Discover why 150,000+ students chose Anderson College. Expert faculty, career services, financial aid, and 10 campuses across Ontario since 1885.',
    canonical: '/why-anderson',
    og: { image: '/assets/heroes/why-anderson-hero.jpg' },
  },
  '/why-anderson/140-years': {
    title: '140 Years of Excellence | Anderson College History',
    description: 'Anderson College has trained career-ready graduates since 1885. Explore our 140-year legacy of practical education and community impact in Ontario.',
    canonical: '/why-anderson/140-years',
    og: { image: '/assets/heroes/anniversary-hero.png' },
  },
  '/why-anderson/land-acknowledgements': {
    title: 'Land Acknowledgements | Anderson College',
    description: 'Anderson College acknowledges the Indigenous peoples of the lands on which our campuses are located across Ontario.',
    canonical: '/why-anderson/land-acknowledgements',
    og: { image: '/assets/heroes/land-acknowledgements-hero.jpg' },
  },
  '/why-anderson/press-corner': {
    title: 'Press Corner | Anderson College Newsroom',
    description: 'Anderson College news, media releases, and press resources. Find the latest announcements, awards, and college milestones.',
    canonical: '/why-anderson/press-corner',
    og: { image: '/assets/heroes/press-corner-hero.jpg' },
  },
  '/why-anderson/faculty': {
    title: 'Our Faculty | Anderson College Industry Experts',
    description: 'Anderson College faculty are working professionals in healthcare, law, business, and technology. Learn from instructors with real-world experience.',
    canonical: '/why-anderson/faculty',
    og: { image: '/assets/heroes/faculty-hero.png' },
  },
  '/why-anderson/testimonials': {
    title: 'Student Testimonials | Anderson College Graduate Stories',
    description: 'Hear from Anderson College graduates. Real stories of career success across healthcare, business, law, and technology programs.',
    canonical: '/why-anderson/testimonials',
    og: { image: '/assets/heroes/testimonials-hero.jpg' },
  },
  // /why-anderson/work-at-anderson was consolidated to /careers in Feb
  // 2026. The server-side 301 fires in frontend/server.js via
  // tier2_redirects.json id=282 so this URL never serves on the public
  // site. The React route in App.js is kept for a one-release safety
  // net but has no SEO entry — anyone who lands on it client-side will
  // get the derived default title in server.js until the route is
  // retired.

  // ── Career Services ───────────────────────
  '/career-services': {
    title: 'Career Services | Anderson College Job Placement Support',
    description: 'Anderson College career services helps graduates land jobs. Resume workshops, employer connections, and lifetime career support for all alumni.',
    canonical: '/career-services',
    og: { image: '/assets/heroes/career-services-hero.jpg' },
  },

  // ── Financial Aid ─────────────────────────
  '/financial-aid': {
    title: 'Financial Aid & Funding | Anderson College Ontario',
    description: 'Explore funding options for your Anderson College diploma. OSAP, bursaries, Better Jobs Ontario, student lines of credit, and more.',
    canonical: '/financial-aid',
    og: { image: '/assets/heroes/financial-aid-hero.png' },
  },
  '/financial-aid/better-jobs-ontario': {
    title: 'Better Jobs Ontario Funding | Anderson College',
    description: 'Anderson College is an approved Better Jobs Ontario training provider. Find out if you qualify for tuition funding for in-demand healthcare and business programs.',
    canonical: '/financial-aid/better-jobs-ontario',
    og: { image: '/assets/heroes/better-jobs-ontario-hero.jpg' },
  },
  '/financial-aid/alumni-bursary': {
    title: 'Alumni Bursary | Anderson College Financial Support',
    description: 'Anderson College alumni bursary provides financial support to eligible returning students. Learn how to apply and what programs qualify.',
    canonical: '/financial-aid/alumni-bursary',
    og: { image: '/assets/heroes/alumni-bursary-hero.png' },
  },
  '/financial-aid/windmill-microlending': {
    title: 'Windmill Microlending | Anderson College Newcomer Funding',
    description: 'Newcomers to Canada can access low-interest loans through Windmill Microlending to fund Anderson College programs. Learn how to apply.',
    canonical: '/financial-aid/windmill-microlending',
    og: { image: '/assets/heroes/windmill-microlending-hero.png' },
  },
  '/financial-aid/new-to-canada': {
    title: 'Funding for Newcomers to Canada | Anderson College',
    description: 'Anderson College supports newcomers to Canada with funding resources, pathway programs, and dedicated admissions support across all Ontario campuses.',
    canonical: '/financial-aid/new-to-canada',
    og: { image: '/assets/heroes/new-to-canada-hero.png' },
  },
  '/financial-aid/student-lines-of-credit': {
    title: 'Student Lines of Credit | Anderson College Financing',
    description: 'Anderson College partners with major banks to offer student lines of credit. Finance your diploma with flexible repayment options.',
    canonical: '/financial-aid/student-lines-of-credit',
    og: { image: '/assets/heroes/student-lines-of-credit-hero.png' },
  },
  '/financial-aid/out-of-province': {
    title: 'Out-of-Province Students | Anderson College Funding',
    description: 'Studying at Anderson College from outside Ontario? Learn about available funding, provincial student loans, and supports for out-of-province students.',
    canonical: '/financial-aid/out-of-province',
    og: { image: '/assets/heroes/out-of-province-students-hero.png' },
  },
  '/financial-aid/cardiology-bursary': {
    title: 'Cardiology Bursary | Anderson College',
    description: 'Financial support available for Cardiology Technology Diploma students at Anderson College. Learn about the cardiology bursary and eligibility.',
    canonical: '/financial-aid/cardiology-bursary',
    og: { image: '/assets/heroes/cardiology-bursary-hero.webp' },
  },

  // ── Campuses ──────────────────────────────
  '/campuses': {
    title: '10 Ontario Campuses | Anderson College Locations',
    description: 'Anderson College has 10 campuses across Ontario including Toronto, Mississauga, Brampton, Hamilton, London, Windsor, Kitchener, and Brantford.',
    canonical: '/campuses',
    og: { image: '/assets/heroes/contact-hero.jpg' },
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE },
        { '@type': 'ListItem', position: 2, name: 'Campuses', item: `${BASE}/campuses` },
      ],
    },
  },
  '/contact': {
    title: 'Contact Anderson College | Phone, Email & Campus Locations',
    description: 'Reach Anderson College: call 1-833-787-1885, email info@andersoncollege.com, book an appointment, or visit one of our 10 Ontario campuses.',
    canonical: '/contact',
    og: { image: '/assets/heroes/contact-hero.jpg' },
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact Anderson College',
      url: `${BASE}/contact`,
      mainEntity: {
        '@type': 'EducationalOrganization',
        '@id': `${BASE}/#organization`,
        name: 'Anderson College of Health, Business and Technology',
        telephone: '+1-833-787-1885',
        email: 'info@andersoncollege.com',
        url: BASE,
      },
    },
  },
  // /former-colleges — heritage + records page for former Westervelt
  // College and Algonquin Career Academy students. Launched Feb 2026.
  '/former-colleges': {
    title: 'Former Colleges | Westervelt & Algonquin Career Academy – Anderson College',
    description: 'Former Westervelt College and Algonquin Career Academy students: Anderson College holds your records. Request transcripts, enrolment verification, and diploma reprints.',
    canonical: '/former-colleges',
    og: { image: '/assets/heroes/contact-hero.jpg' },
  },
  // /convocation-tickets — event-specific landing pages. Reachable
  // (served via server.js utility whitelist) but noindex so they don't
  // show up in search for stale/past events.
  '/convocation-tickets': {
    title: 'Convocation Tickets | Anderson College',
    description: 'Convocation ticket information for Anderson College graduates and guests.',
    canonical: '/convocation-tickets',
    noIndex: true,
  },
  '/convocation-tickets/NY': {
    title: 'Convocation Tickets — North York Campus | Anderson College',
    description: 'North York campus convocation ticket information.',
    canonical: '/convocation-tickets/NY',
    noIndex: true,
  },
  // Dedicated admissions-requirements landing page for the Cardiology
  // Technologist program. 1:1 port of the legacy
  // /cardio_admissions_requirements/ URL — kept at this nested path so
  // the content is grouped with the program and discoverable via the
  // program navigation. /cardio_admissions_requirements 301s here in
  // one hop (tier1 id=14).
  '/programs/cardiology-technologist/cardio_admissions_requirements': {
    title: 'Cardiology Technologist Admissions Requirements | Anderson College',
    description: 'Admissions requirements for the Cardiology Technologist program at Anderson College — academic prerequisites, non-academic requirements, and application steps.',
    canonical: '/programs/cardiology-technologist/cardio_admissions_requirements',
    og: { image: '/assets/heroes/cardiology-technologist-hero.webp' },
  },

  // ── Blog ──────────────────────────────────
  '/blog': {
    title: 'Healthcare & Career Blog | Anderson College',
    description: 'Anderson College blog: career tips, program spotlights, student stories, and healthcare industry insights to guide your education and career journey.',
    canonical: '/blog',
    og: { image: '/assets/brand/hero-graduate.jpg' },
  },

  // ── Student Life ──────────────────────────
  '/student-life': {
    title: 'Student Life | Anderson College Ontario',
    description: 'Discover student life at Anderson College. Resources, clubs, financial support, digital credentials, and a welcoming community across 10 Ontario campuses.',
    canonical: '/student-life',
    og: { image: '/assets/heroes/resources-hero.jpg' },
  },
  '/student-life/need-help': {
    title: 'Student Support & Help | Anderson College',
    description: 'Anderson College offers academic, personal, and career support to all students. Connect with our student services team at any Ontario campus.',
    canonical: '/student-life/need-help',
    og: { image: '/assets/heroes/need-help-hero.jpg' },
  },
  '/student-life/gmssp': {
    title: 'GMSSP Program | Anderson College',
    description: 'The Government-Mandated Student Support Program (GMSSP) at Anderson College provides essential student services funded under Ontario regulation.',
    canonical: '/student-life/gmssp',
    og: { image: '/assets/heroes/gmssp-hero.png' },
  },
  '/student-life/digital-credentials-faq': {
    title: 'Digital Credentials FAQ | Anderson College',
    description: 'Anderson College issues verifiable digital credentials. Learn how your diploma or certificate is issued, shared, and verified digitally.',
    canonical: '/student-life/digital-credentials-faq',
    og: { image: '/assets/heroes/digital-credentials-hero.png' },
  },
  '/student-life/resources': {
    title: 'Student Resources | Anderson College Ontario',
    description: 'Access financial aid forms, wellness resources, campus contacts, and academic support tools for Anderson College students.',
    canonical: '/student-life/resources',
    og: { image: '/assets/heroes/resources-hero.jpg' },
  },
  '/student-life/opswa-membership': {
    title: 'OPSWA Membership | Anderson College',
    description: 'Anderson College PSW students receive OPSWA membership. Learn about the benefits of Ontario PSW Association membership for your career.',
    canonical: '/student-life/opswa-membership',
    og: { image: '/assets/heroes/opswa-hero.webp' },
  },
  '/student-life/odaa-membership': {
    title: 'ODAA Membership | Anderson College Dental Students',
    description: 'Anderson College dental students receive Ontario Dental Assistants Association (ODAA) membership. Discover the professional benefits.',
    canonical: '/student-life/odaa-membership',
    og: { image: '/assets/heroes/odaa-hero.webp' },
  },
  '/student-life/bookstore': {
    title: 'Campus Bookstore | Anderson College',
    description: 'Access textbooks, program supplies, and campus merchandise through the Anderson College bookstore.',
    canonical: '/student-life/bookstore',
    og: { image: '/assets/brand/hero-graduate.jpg' },
  },
  '/student-life/share-your-story': {
    title: 'Share Your Story | Anderson College Graduate Stories',
    description: 'Are you an Anderson College graduate? Share your career journey and inspire the next generation of students.',
    canonical: '/student-life/share-your-story',
    og: { image: '/assets/brand/hero-graduate.jpg' },
  },

  // ── Other public pages ────────────────────
  '/student-stories': {
    title: 'Student Success Stories | Anderson College Graduates',
    description: 'Read real success stories from Anderson College graduates working in healthcare, business, law, and technology across Ontario.',
    canonical: '/student-stories',
    og: { image: '/assets/heroes/testimonials-hero.jpg' },
  },
  '/events': {
    title: 'Events & Open Houses | Anderson College Ontario',
    description: 'Find upcoming open houses, info sessions, and events at Anderson College campuses across Ontario. Register for a free campus visit today.',
    canonical: '/events',
    og: { image: '/assets/brand/hero-graduate.jpg' },
  },
  '/careers': {
    title: 'Career Opportunities | Work at Anderson College',
    description: 'View current job openings at Anderson College across Ontario. We hire faculty, administrators, and support staff at 10 campus locations.',
    canonical: '/careers',
    og: { image: '/assets/heroes/work-at-anderson-hero.webp' },
  },
  '/discovery-quiz': {
    title: 'Find Your Program | Anderson College Program Quiz',
    description: 'Not sure which program is right for you? Take the Anderson College program discovery quiz and get a personalised recommendation in under 3 minutes.',
    canonical: '/discovery-quiz',
    og: { image: '/assets/brand/hero-graduate.jpg' },
  },
  '/book-a-tour': {
    title: 'Book a Campus Tour | Anderson College Ontario',
    description: 'Tour an Anderson College campus near you. Book a free guided visit to see our facilities, meet faculty, and ask your admissions questions.',
    canonical: '/book-a-tour',
    og: { image: '/assets/heroes/contact-hero.jpg' },
  },
  '/step-up': {
    title: 'Step Up Program | Anderson College',
    description: 'The Anderson College Step Up program offers accelerated pathways for students ready to advance their healthcare or business careers.',
    canonical: '/step-up',
    og: { image: '/assets/brand/hero-graduate.jpg' },
  },
  '/student-portal': {
    title: 'Student Portal & Logins | Anderson College',
    description: 'Log in to the Anderson College student portal, LMS and D2L, or get help from the student helpdesk. All your student logins in one place.',
    canonical: '/student-portal',
    og: { image: '/assets/brand/hero-graduate.jpg' },
  },
  '/search': {
    title: 'Search | Anderson College',
    description: 'Search programs, blog posts, campuses, and resources across the Anderson College website.',
    canonical: '/search',
    og: { image: '/assets/brand/hero-graduate.jpg' },
  },
  '/privacy': {
    title: 'Privacy Policy | Anderson College',
    description: 'Read the Anderson College privacy policy. Learn how we collect, use, and protect your personal information.',
    canonical: '/privacy',
    og: { image: '/assets/brand/hero-graduate.jpg' },
  },
  '/terms': {
    title: 'Terms of Use | Anderson College',
    description: 'Review the terms and conditions for using the Anderson College website and digital services.',
    canonical: '/terms',
    og: { image: '/assets/brand/hero-graduate.jpg' },
  },
  '/legal/cookie-policy': {
    title: 'Cookie Policy | Anderson College',
    description: 'Anderson College cookie policy — how we use cookies and tracking technologies on our website.',
    canonical: '/legal/cookie-policy',
    og: { image: '/assets/brand/hero-graduate.jpg' },
  },

  // ── Conversion landing pages ───────────────
  '/start/medical-lab-technologist': {
    title: 'Medical Lab Technologist Diploma | Anderson College',
    description: 'Start your Medical Laboratory Technology career at Anderson College. EQual-accredited diploma, advanced lab training, and CMLTO registration prep.',
    canonical: '/start/medical-lab-technologist',
    og: { image: '/assets/programs/batch2/hero-mlt.jpg' },
    noIndex: false, // flip to true if you want to keep landing pages out of index
  },

  // ── Sitemap (human-readable HTML site index) ────────
  // Complements /sitemap.xml. Indexed so it's a second discovery
  // path for crawlers plus a proper user-facing page.
  '/sitemap': {
    title: 'Sitemap | Anderson College',
    description: 'Complete index of every page on the Anderson College website — programs, campuses, admissions info, blog, and more.',
    canonical: '/sitemap',
    noIndex: false,
  },

  // ── Graduation FAQ (published 2026) ──────────────────
  // Consolidated replacement for 3 legacy WordPress FAQ pages. Two
  // 301 redirects already forward legacy URLs to this page (see
  // /app/frontend/src/data/tier2_redirects.json). Indexed and
  // discoverable via /search + XML sitemap; not linked in the main
  // nav because this is a graduate-audience page surfaced through
  // convocation emails and direct links.
  //
  // Titles below mirror the <PageSEO> blocks inside GraduationFaq.jsx
  // / FacultyFaq.jsx — RouteHelmet also renders a PageSEO, so if the
  // two drift the stale copy gets baked into the prerender HTML
  // (observed on prod 2026-10 before the sync — og:title read
  // "Convocation" while <title> read "Graduation"). Keep both ends
  // of the pair identical when either changes.
  '/graduation-faq': {
    title: 'Graduation FAQ | Anderson College',
    description: 'Everything Anderson College graduates and their guests need to know about convocation — ceremony dates, tickets, dress code, guest access, and on-the-day logistics.',
    canonical: '/graduation-faq',
    noIndex: false,
  },
  '/graduation-faq/faculty': {
    title: 'Staff & Faculty Graduation FAQ | Anderson College',
    description: 'Anderson College staff and faculty guide to convocation — attendance, roles, dress code, tickets for guests, and venue logistics.',
    canonical: '/graduation-faq/faculty',
    noIndex: false,
  },

  // ── Fall 2026 Bursary — standalone funding page ─────────────────
  // Previously relied on the global "/" fallback, which indexed the
  // homepage <title> for /financial-aid/fall-bursary. Oct-2026 crawl.
  '/financial-aid/fall-bursary': {
    title: 'Fall 2026 Bursary — Save up to $3,000 | Anderson College',
    description: 'Enrol in a qualifying Anderson College diploma between September 1 and October 31, 2026 and receive up to $3,000 off tuition on graduation. Speak to an Advisor to learn more.',
    canonical: '/financial-aid/fall-bursary',
  },

  // ── Student Credential Verification (digital credentials FAQ) ───
  // Deep sub-page of /student-life/digital-credentials-faq. Oct-2026
  // crawl also found this falling through to the homepage title.
  '/student-life/digital-credentials-faq/student-credential-verification': {
    title: 'Verify a Student Credential | Anderson College',
    description: 'Verify an Anderson College digital credential or diploma. Enter the credential ID or scan the QR code on the digital badge to confirm it was issued by Anderson College.',
    canonical: '/student-life/digital-credentials-faq/student-credential-verification',
  },

  // ── /language/* — Department of Languages (ESL) ─────────────────
  // Preserves the retired WordPress URLs exactly (including the
  // underscore in `/language/why_anderson/`). Titles + descriptions
  // are the same strings surfaced by languageRoutes.js so the
  // placeholder page's H1 and the <title> tag stay in sync.
  '/language/':                                             { title: 'The Department of Languages — Anderson College',              description: "Learn English at Anderson College's Department of Languages in downtown Toronto. General English, IELTS Test Preparation, and Conversation Café programs.", canonical: '/language/' },
  '/language/programs/':                                    { title: 'Programs Archive - Anderson Department Of Languages',        description: "Choose from three flexible ESL programs at Anderson College: General English for all levels, IELTS Test Preparation, and the Conversation Café.", canonical: '/language/programs/' },
  '/language/programs/general-english-for-all-levels/':     { title: 'General English for All Levels | Anderson Language',         description: "Build a strong foundation in English at Anderson College — small classes, warm community, four intake dates a year in downtown Toronto.", canonical: '/language/programs/general-english-for-all-levels/' },
  '/language/programs/ielts-test-preparation/':             { title: 'IELTS Test Preparation | Anderson Language',                 description: "Prepare for IELTS Academic and General with Anderson College's IELTS test prep program in Toronto. Test-centre-approved instructors, real practice exams.", canonical: '/language/programs/ielts-test-preparation/' },
  '/language/programs/english-conversation-cafe/':          { title: 'English Conversation Café | Anderson Language',              description: "Speak with confidence. Anderson College's Conversation Café is a small-group, immersive ESL program in downtown Toronto.", canonical: '/language/programs/english-conversation-cafe/' },
  '/language/why_anderson/':                                { title: 'Why Anderson',                                                description: 'Family-owned, downtown Toronto, warm community, small classes. See why students choose Anderson College\'s Department of Languages.', canonical: '/language/why_anderson/' },
  '/language/testimonials/':                                { title: 'Testimonials - Anderson Department Of Languages',            description: "Hear from real students at Anderson College's Department of Languages about their journey learning English in Toronto.", canonical: '/language/testimonials/' },
  '/language/faculty/':                                     { title: 'Faculty - Anderson Department Of Languages',                 description: "Meet the ESL instructors at Anderson College's Department of Languages — TESL-certified, warm, and student-focused.", canonical: '/language/faculty/' },
  '/language/student-life/':                                { title: 'Student Life - Anderson Department Of Languages',            description: 'Downtown Toronto ESL student life: events, TTC access, cafés, museums, keep.meSAFE 24/7 mental-health support, and a warm community.', canonical: '/language/student-life/' },
  '/language/keep-mesafe/':                                 { title: 'keep.meSAFE - Anderson Department Of Languages',             description: '24/7 mental-health and wellbeing support for ESL students at Anderson College — free, confidential, in your language.', canonical: '/language/keep-mesafe/' },
  '/language/resources/':                                   { title: 'Resources - Anderson Department Of Languages',               description: 'Bookstore, tech help, tutoring, transit passes, and downloadable resources for Anderson College language students.', canonical: '/language/resources/' },
  '/language/admissions/':                                  { title: 'Admissions - Anderson Department Of Languages',              description: "Apply to Anderson College's Department of Languages. Simple steps, fast decisions, admission support in your language.", canonical: '/language/admissions/' },
  '/language/payment-portal-2-2/':                          { title: 'Payment Portal - Anderson Department Of Languages',          description: 'Pay tuition and fees for your Anderson College language program securely online.', canonical: '/language/payment-portal-2-2/' },
  '/language/privacy-policy/':                              { title: 'Privacy Policy - Anderson Department Of Languages',          description: "How Anderson College's Department of Languages collects, uses, and protects your personal information.", canonical: '/language/privacy-policy/' },
  '/language/terms-of-use/':                                { title: 'Terms of Use - Anderson Department Of Languages',            description: "The terms governing your use of Anderson College's Department of Languages website.", canonical: '/language/terms-of-use/' },
  '/language/sitemap/':                                     { title: 'Sitemap - Anderson Department Of Languages',                 description: "A quick overview of every page on Anderson College's Department of Languages site.", canonical: '/language/sitemap/' },
};

/**
 * getSEO(pathname)
 * Resolves the correct SEO config for any route.
 *
 * Handles:
 *   - Exact static routes (STATIC_SEO)
 *   - /programs/:slug  (PROGRAM_SEO)
 *   - /blog/:slug      (fallback — ideally override with CMS data per post)
 *   - /campuses/:slug  (fallback)
 *
 * Every non-home route also carries a BreadcrumbList, either hard-
 * coded on the entry or derived from the pathname by `_withBreadcrumb`.
 *
 * @param {string} pathname  — from useLocation().pathname
 * @returns {object}         — SEO props object for <PageSEO />
 */
// ─────────────────────────────────────────────
// Derive a BreadcrumbList from any pathname
// ─────────────────────────────────────────────
// Per 2026-02 structured-data cleanup: every public route carries a
// BreadcrumbList. Hard-coding one per entry in STATIC_SEO was too
// repetitive, so the breadcrumb is now derived from the path when
// the entry doesn't already carry one. Walks the path left to right
// and resolves each prefix via STATIC_SEO so sub-section titles
// come from the authoritative source.
const _CRUMB_SECTION_LABEL = {
  '': 'Home',
  'why-anderson': 'Why Anderson',
  'financial-aid': 'Financial Aid',
  'admissions': 'Admissions',
  'career-services': 'Career Services',
  'campuses': 'Campuses',
  'programs': 'Programs',
  'contact': 'Contact',
  'blog': 'Blog',
  'discovery': 'Program Finder',
  'convocation-tickets': 'Convocation Tickets',
  'former-colleges': 'Former Colleges',
  'language': 'Anderson English Academy',
  'apply': 'Apply',
  'careers': 'Careers',
  'refer-a-friend': 'Refer a Friend',
  'graduation-faq': 'Graduation FAQ',
  'student-life': 'Student Life',
};

function _titleCaseSlug(s) {
  if (!s) return s;
  return s.split('-').map((w) => (w.length <= 2 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1))).join(' ');
}

function _crumbLabelFor(prefix, segment) {
  if (prefix.indexOf('/') === -1 && _CRUMB_SECTION_LABEL[prefix]) {
    return _CRUMB_SECTION_LABEL[prefix];
  }
  const entry = STATIC_SEO['/' + prefix];
  if (entry && entry.title) {
    return entry.title
      .replace(/ \| .*$/, '')
      .replace(/ – .*$/, '')
      .replace(/ Diploma.*$/, '')
      .trim();
  }
  return _titleCaseSlug(segment);
}

function _deriveBreadcrumb(pathname) {
  if (!pathname || pathname === '/' || pathname === '') return null;
  const segments = pathname.replace(/^\/+|\/+$/g, '').split('/');
  if (!segments[0]) return null;
  const items = [{ '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE}/` }];
  let prefix = '';
  segments.forEach((seg, i) => {
    prefix = prefix ? `${prefix}/${seg}` : seg;
    items.push({
      '@type': 'ListItem',
      position: i + 2,
      name: _crumbLabelFor(prefix, seg),
      item: `${BASE}/${prefix}`,
    });
  });
  return {
    '@type': 'BreadcrumbList',
    '@id': `${BASE}${pathname}#breadcrumb`,
    itemListElement: items,
  };
}

function _entryHasBreadcrumb(entry) {
  const j = entry && entry.jsonLd;
  if (!j) return false;
  if (j['@type'] === 'BreadcrumbList') return true;
  const graph = Array.isArray(j['@graph']) ? j['@graph'] : null;
  if (graph && graph.some((n) => n && n['@type'] === 'BreadcrumbList')) return true;
  return false;
}

function _withBreadcrumb(entry, pathname) {
  if (!entry) return entry;
  if (_entryHasBreadcrumb(entry)) return entry;
  const bc = _deriveBreadcrumb(pathname);
  if (!bc) return entry;
  if (!entry.jsonLd) return { ...entry, jsonLd: { '@context': 'https://schema.org', '@graph': [bc] } };
  if (Array.isArray(entry.jsonLd['@graph'])) {
    return { ...entry, jsonLd: { ...entry.jsonLd, '@graph': [...entry.jsonLd['@graph'], bc] } };
  }
  const existing = { ...entry.jsonLd };
  delete existing['@context'];
  return { ...entry, jsonLd: { '@context': 'https://schema.org', '@graph': [existing, bc] } };
}

export function getSEO(pathname) {
  // Normalize trailing slash BEFORE lookup. Server-side we 301 any
  // non-root trailing slash to the no-slash form, but RouteHelmet runs
  // in the browser where client-side navigation can arrive here with
  // or without the slash. Previously a path like "/programs/" fell
  // through every pattern (STATIC_SEO['/programs/'] miss → not a
  // combo → programMatch failed since `[^/]+` needs ≥1 char → ...)
  // all the way to the "/" global fallback, so bots indexed /programs/
  // with the homepage title + canonical. The normaliser below keeps
  // the trailing slash ONLY for the /language/* namespace where the
  // trailing slash is part of the canonical URL per batch-0.
  const p =
    pathname.length > 1 && pathname.endsWith("/") && !pathname.startsWith("/language")
      ? pathname.slice(0, -1)
      : pathname;

  // Exact match (try both the as-received and the normalised form so a
  // legitimate /language/foo/ hit still resolves even if a caller
  // accidentally strips the slash somewhere up the stack).
  if (STATIC_SEO[pathname]) return _withBreadcrumb(STATIC_SEO[pathname], pathname);
  if (p !== pathname && STATIC_SEO[p]) return _withBreadcrumb(STATIC_SEO[p], p);

  // From here on all pattern checks use the normalised `p`.

  // Program × Campus combo pages (/programs/:program/:campus) — the
  // ProgramCampusTemplate mounts its own <PageSEO> with a combo-scoped
  // title, description, canonical, and EducationalOccupationalProgram
  // + hasCourseInstance + BreadcrumbList JSON-LD. Returning the parent
  // program's SEO here would duplicate tags. RouteHelmet also short-
  // circuits this pattern (see components/RouteHelmet.jsx).
  if (/^\/programs\/[^/]+\/[^/]+$/.test(p)) {
    return null;
  }

  // Program pages — lazily-loaded map. If the map hasn't hydrated yet,
  // return a lightweight fallback (no JSON-LD). RouteHelmet.jsx calls
  // ensureProgramSEO() on program routes and re-renders once the map
  // resolves, at which point this function returns the full entry with
  // Course + EducationalOccupationalProgram @graph.
  const programMatch = p.match(/^\/programs\/([^/]+)$/);
  if (programMatch) {
    const slug = programMatch[1];
    const map = getLoadedProgramSEO();
    if (map && map[slug]) return _withBreadcrumb(map[slug], p);
    return _withBreadcrumb({
      title: 'Program | Anderson College Ontario',
      description: 'Explore career-focused diploma programs at Anderson College across 10 Ontario campuses.',
      canonical: p,
    }, p);
  }

  // Blog detail — best practice is to pass CMS data directly to <PageSEO>
  // from within BlogDetailPage.jsx. This is a safe fallback.
  const blogMatch = p.match(/^\/blog\/([^/]+)$/);
  if (blogMatch) {
    return _withBreadcrumb({
      title: 'Blog | Anderson College',
      description: 'Career tips, program spotlights, and student stories from Anderson College.',
      canonical: p,
      og: { image: '/assets/brand/hero-graduate.jpg' },
    }, p);
  }

  // Campus detail fallback
  const campusMatch = p.match(/^\/campuses\/([^/]+)$/);
  if (campusMatch) {
    const campusName = campusMatch[1]
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    // No `_withBreadcrumb` wrap — the `CampusLocalBusinessSchema`
    // component already emits the campus BreadcrumbList inside its
    // @graph on mount (CampusDetailPage). Returning one here too
    // would land two identical BreadcrumbList blocks on the page.
    return {
      title: `${campusName} Campus | Anderson College Ontario`,
      description: `Visit Anderson College in ${campusName}. Diploma programs in healthcare, business, and technology. Book a free campus tour today.`,
      canonical: p,
      og: { image: '/assets/heroes/contact-hero.jpg' },
    };
  }

  // Discovery results fallback
  if (p.startsWith('/discovery/results/')) {
    return _withBreadcrumb({
      title: 'Your Program Match | Anderson College',
      description: 'Based on your answers, here are the Anderson College programs that best match your goals.',
      canonical: p,
    }, p);
  }

  // Global fallback
  return STATIC_SEO['/'];
}
